import type { UserNode } from '@revelio/data-access';

import { findFilterById } from '../../../filters/engine/filterRegistry';
import {
  type Filter,
  FilterId,
  FilterType,
  isMultiFilter,
  type ListFilter,
  type StaticListFilter,
} from '../../../filters/types/filters';
import { isListFilter, isStaticListFilter } from '../filter-type-guards';
import { idsToNode, wrapExprNode } from './node-builders';
import { TalentDiscoveryV3Tags } from './types';

/** Extract numeric selections from ListFilter or StaticListFilter */
const extractNumericSelections = (
  filter: ListFilter | StaticListFilter | undefined
): number[] => {
  if (!filter) return [];

  const selections = filter.selections;
  if (!Array.isArray(selections)) return [];

  return selections
    .map((s) => {
      const id = (s as { id?: string | number })?.id;
      const n = typeof id === 'string' ? Number(id) : id;
      return typeof n === 'number' && Number.isFinite(n) ? n : null;
    })
    .filter((n): n is number => n !== null);
};

/**
 * Build a list filter UserNode from a ListFilter or StaticListFilter.
 * Requires the caller to ensure the filter is the correct type.
 * @param supportsCurrent - Whether this filter type supports current/non-current. Defaults to true.
 */
const buildListFilter = ({
  filter,
  tag,
  supportsCurrent = true,
}: {
  filter: ListFilter | StaticListFilter;
  tag: TalentDiscoveryV3Tags;
  supportsCurrent?: boolean;
}): UserNode | null => {
  const ids = extractNumericSelections(filter);

  // For filters that don't support current/non-current, don't pass isCurrent
  const curr = supportsCurrent ? filter.isCurrent : undefined;
  const exprNode = idsToNode(ids, tag, curr);

  if (!exprNode) {
    return null;
  }

  const isNot = 'isNot' in filter ? filter.isNot : false;
  return wrapExprNode(exprNode, isNot);
};

/**
 * Find a filter by ID and build a list filter UserNode if it's a ListFilter or StaticListFilter.
 * @param supportsCurrent - Whether this filter type supports current/non-current. Defaults to true.
 */
export const findAndBuildListFilter = (
  filters: Filter[],
  filterId: FilterId,
  tag: TalentDiscoveryV3Tags,
  supportsCurrent = true
): UserNode | null => {
  const filter = findFilterById(filters, filterId);
  if (!isListFilter(filter) && !isStaticListFilter(filter)) {
    return null;
  }
  return buildListFilter({ filter, tag, supportsCurrent });
};

export const buildMultiEmploymentRelationshipFilter = (
  filters: Filter[]
): UserNode | UserNode[] | null => {
  const multiEmploymentRelationship = findFilterById(
    filters,
    FilterId.MULTI_EMPLOYMENT_RELATIONSHIP
  );

  if (
    !multiEmploymentRelationship ||
    !isMultiFilter(multiEmploymentRelationship)
  ) {
    return null;
  }

  if (multiEmploymentRelationship.instances.length === 0) {
    return null;
  }

  const instanceNodes = multiEmploymentRelationship.instances
    .filter((inst): inst is ListFilter => inst?.type === FilterType.LIST)
    .map((instance) => {
      return buildListFilter({
        filter: instance,
        tag: TalentDiscoveryV3Tags.EmploymentRelationship,
      });
    })
    .filter((node): node is UserNode => node !== null);

  if (instanceNodes.length === 0) return null;
  if (instanceNodes.length === 1) {
    const [singleNode] = instanceNodes;
    return singleNode ?? null;
  }

  return instanceNodes;
};

export const buildMultiWorkScheduleFilter = (
  filters: Filter[]
): UserNode | UserNode[] | null => {
  const multiWorkSchedule = findFilterById(
    filters,
    FilterId.MULTI_WORK_SCHEDULE
  );

  if (!multiWorkSchedule || !isMultiFilter(multiWorkSchedule)) {
    return null;
  }

  if (multiWorkSchedule.instances.length === 0) {
    return null;
  }

  const instanceNodes = multiWorkSchedule.instances
    .filter((inst): inst is ListFilter => inst?.type === FilterType.LIST)
    .map((instance) => {
      return buildListFilter({
        filter: instance,
        tag: TalentDiscoveryV3Tags.WorkSchedule,
      });
    })
    .filter((node): node is UserNode => node !== null);

  if (instanceNodes.length === 0) return null;
  if (instanceNodes.length === 1) {
    const [singleNode] = instanceNodes;
    return singleNode ?? null;
  }

  return instanceNodes;
};

/**
 * Build education filter node from filter state
 * Education doesn't support current/non-current
 */
export const buildEducationFilter = (filters: Filter[]): UserNode | null => {
  return findAndBuildListFilter(
    filters,
    FilterId.EDUCATION,
    TalentDiscoveryV3Tags.HighestDegree,
    false
  );
};

/**
 * Build seniority filter node from filter state
 */
export const buildSeniorityFilter = (filters: Filter[]): UserNode | null => {
  return findAndBuildListFilter(
    filters,
    FilterId.SENIORITY,
    TalentDiscoveryV3Tags.Seniority
  );
};

/**
 * Build multi seniority filter node from filter state.
 * Each instance becomes a node, and instances are ANDed together.
 * Returns an array of nodes when there are multiple instances (to avoid nested AND nodes).
 */
export const buildMultiSeniorityFilter = (
  filters: Filter[]
): UserNode | UserNode[] | null => {
  const multiSeniority = findFilterById(filters, FilterId.MULTI_SENIORITY);

  if (!multiSeniority || !isMultiFilter(multiSeniority)) {
    return null;
  }

  if (multiSeniority.instances.length === 0) {
    return null;
  }

  const instanceNodes = multiSeniority.instances
    .filter((inst): inst is ListFilter => inst?.type === FilterType.LIST)
    .map((instance) => {
      return buildListFilter({
        filter: instance,
        tag: TalentDiscoveryV3Tags.Seniority,
      });
    })
    .filter((node): node is UserNode => node !== null);

  if (instanceNodes.length === 0) return null;
  if (instanceNodes.length === 1) {
    const [singleNode] = instanceNodes;
    return singleNode ?? null;
  }

  return instanceNodes;
};

/**
 * Build multi flight risk filter node from filter state.
 * Each instance becomes a node, and instances are ANDed together.
 * Returns an array of nodes when there are multiple instances (to avoid nested AND nodes).
 */
export const buildMultiFlightRiskFilter = (
  filters: Filter[]
): UserNode | UserNode[] | null => {
  const multiFlightRisk = findFilterById(filters, FilterId.MULTI_FLIGHT_RISK);

  if (!multiFlightRisk || !isMultiFilter(multiFlightRisk)) {
    return null;
  }

  if (multiFlightRisk.instances.length === 0) {
    return null;
  }

  const instanceNodes = multiFlightRisk.instances
    .filter(
      (inst): inst is StaticListFilter => inst?.type === FilterType.STATIC_LIST
    )
    .map((instance) => {
      return buildListFilter({
        filter: instance,
        tag: TalentDiscoveryV3Tags.FlightRisk,
      });
    })
    .filter((node): node is UserNode => node !== null);

  if (instanceNodes.length === 0) return null;
  if (instanceNodes.length === 1) {
    const [singleNode] = instanceNodes;
    return singleNode ?? null;
  }

  return instanceNodes;
};

/**
 * Build multi prestige filter node from filter state.
 * Each instance becomes a node, and instances are ANDed together.
 * Returns an array of nodes when there are multiple instances (to avoid nested AND nodes).
 */
export const buildMultiPrestigeFilter = (
  filters: Filter[]
): UserNode | UserNode[] | null => {
  const multiPrestige = findFilterById(filters, FilterId.MULTI_PRESTIGE);

  if (!multiPrestige || !isMultiFilter(multiPrestige)) {
    return null;
  }

  if (multiPrestige.instances.length === 0) {
    return null;
  }

  const instanceNodes = multiPrestige.instances
    .filter(
      (inst): inst is StaticListFilter => inst?.type === FilterType.STATIC_LIST
    )
    .map((instance) => {
      return buildListFilter({
        filter: instance,
        tag: TalentDiscoveryV3Tags.Prestige,
      });
    })
    .filter((node): node is UserNode => node !== null);

  if (instanceNodes.length === 0) return null;
  if (instanceNodes.length === 1) {
    const [singleNode] = instanceNodes;
    return singleNode ?? null;
  }

  return instanceNodes;
};

/**
 * Build multi remote suitability filter node from filter state.
 * Each instance becomes a node, and instances are ANDed together.
 * Returns an array of nodes when there are multiple instances (to avoid nested AND nodes).
 */
export const buildMultiRemoteSuitabilityFilter = (
  filters: Filter[]
): UserNode | UserNode[] | null => {
  const multiRemoteSuitability = findFilterById(
    filters,
    FilterId.MULTI_REMOTE_SUITABILITY
  );

  if (!multiRemoteSuitability || !isMultiFilter(multiRemoteSuitability)) {
    return null;
  }

  if (multiRemoteSuitability.instances.length === 0) {
    return null;
  }

  const instanceNodes = multiRemoteSuitability.instances
    .filter(
      (inst): inst is StaticListFilter => inst?.type === FilterType.STATIC_LIST
    )
    .map((instance) => {
      return buildListFilter({
        filter: instance,
        tag: TalentDiscoveryV3Tags.RemoteSuitability,
      });
    })
    .filter((node): node is UserNode => node !== null);

  if (instanceNodes.length === 0) return null;
  if (instanceNodes.length === 1) {
    const [singleNode] = instanceNodes;
    return singleNode ?? null;
  }

  return instanceNodes;
};

/**
 * Build multi gender filter node from filter state.
 * Each instance becomes a node, and instances are ANDed together.
 * Returns an array of nodes when there are multiple instances (to avoid nested AND nodes).
 */
export const buildMultiGenderFilter = (
  filters: Filter[]
): UserNode | UserNode[] | null => {
  const multiGender = findFilterById(filters, FilterId.MULTI_GENDER);

  if (!multiGender || !isMultiFilter(multiGender)) {
    return null;
  }

  if (multiGender.instances.length === 0) {
    return null;
  }

  const instanceNodes = multiGender.instances
    .filter((inst): inst is ListFilter => inst?.type === FilterType.LIST)
    .map((instance) => {
      return buildListFilter({
        filter: instance,
        tag: TalentDiscoveryV3Tags.Gender,
        supportsCurrent: false,
      });
    })
    .filter((node): node is UserNode => node !== null);

  if (instanceNodes.length === 0) return null;
  if (instanceNodes.length === 1) {
    const [singleNode] = instanceNodes;
    return singleNode ?? null;
  }

  return instanceNodes;
};

/**
 * Build multi ethnicity filter node from filter state.
 * Each instance becomes a node, and instances are ANDed together.
 * Returns an array of nodes when there are multiple instances (to avoid nested AND nodes).
 */
export const buildMultiEthnicityFilter = (
  filters: Filter[]
): UserNode | UserNode[] | null => {
  const multiEthnicity = findFilterById(filters, FilterId.MULTI_ETHNICITY);

  if (!multiEthnicity || !isMultiFilter(multiEthnicity)) {
    return null;
  }

  if (multiEthnicity.instances.length === 0) {
    return null;
  }

  const instanceNodes = multiEthnicity.instances
    .filter((inst): inst is ListFilter => inst?.type === FilterType.LIST)
    .map((instance) => {
      return buildListFilter({
        filter: instance,
        tag: TalentDiscoveryV3Tags.Ethnicity,
        supportsCurrent: false,
      });
    })
    .filter((node): node is UserNode => node !== null);

  if (instanceNodes.length === 0) return null;
  if (instanceNodes.length === 1) {
    const [singleNode] = instanceNodes;
    return singleNode ?? null;
  }

  return instanceNodes;
};

/**
 * Build multi education filter node from filter state.
 * Each instance becomes a node, and instances are ANDed together.
 * Returns an array of nodes when there are multiple instances (to avoid nested AND nodes).
 */
export const buildMultiEducationFilter = (
  filters: Filter[]
): UserNode | UserNode[] | null => {
  const multiEducation = findFilterById(filters, FilterId.MULTI_EDUCATION);

  if (!multiEducation || !isMultiFilter(multiEducation)) {
    return null;
  }

  if (multiEducation.instances.length === 0) {
    return null;
  }

  const instanceNodes = multiEducation.instances
    .filter((inst): inst is ListFilter => inst?.type === FilterType.LIST)
    .map((instance) => {
      return buildListFilter({
        filter: instance,
        tag: TalentDiscoveryV3Tags.HighestDegree,
        supportsCurrent: false,
      });
    })
    .filter((node): node is UserNode => node !== null);

  if (instanceNodes.length === 0) return null;
  if (instanceNodes.length === 1) {
    const [singleNode] = instanceNodes;
    return singleNode ?? null;
  }

  return instanceNodes;
};
